"use strict";
/**
 * Broker-creation invariants — Step 7 of the May 2026 grain-separation fix.
 *
 * Defense-in-depth boundary: every Firestore broker-creation path MUST call
 * `assertBrokerCreatePayload` before writing the broker doc. The validator
 * refuses the synthetic 'Unknown Broker' / 'unknown' patterns that historically
 * polluted the broker dropdown via the old parser's `|| 'unknown'` fallback.
 *
 * The same prevention exists at three layers (Step 6 plan):
 *   1. Parser refuses non-loan rows at write time (raw_commissions sink).
 *   2. NOT NULL on raw_commissions.broker_id at the BQ schema layer.
 *   3. **This validator** — refuses to create the synthetic broker doc that
 *      such a pollution would necessitate.
 *
 * Intentionally provider-agnostic — pure functions, no Firestore imports.
 * Callers wrap the validation around their `.set()` / `.create()` calls.
 *
 * Reference: ~/.claude/plans/review-on-this-issue-wobbly-pie.md
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.BrokerInvariantViolation = exports.BROKER_SENTINEL_IDS = exports.BROKER_SENTINEL_NAMES = void 0;
exports.assertBrokerCreatePayload = assertBrokerCreatePayload;
exports.describeBrokerSentinel = describeBrokerSentinel;
/**
 * Reserved sentinel values that must never appear as a broker identity.
 * Lowercased + trimmed for the comparison.
 */
exports.BROKER_SENTINEL_NAMES = new Set([
    'unknown',
    'unknown broker',
    'n/a',
    'na',
    'null',
    'tbc',
    'tbd',
]);
exports.BROKER_SENTINEL_IDS = new Set([
    'unknown',
    'n/a',
    'null',
]);
class BrokerInvariantViolation extends Error {
    field;
    offendingValue;
    constructor(field, offendingValue, message) {
        super(message);
        this.field = field;
        this.offendingValue = offendingValue;
        this.name = 'BrokerInvariantViolation';
    }
}
exports.BrokerInvariantViolation = BrokerInvariantViolation;
/**
 * Pure validator. Throws `BrokerInvariantViolation` when the payload would
 * write a synthetic-broker sentinel. Returns void on valid payloads.
 *
 * Use this at every Firestore broker-write boundary:
 *   ```ts
 *   assertBrokerCreatePayload({ id: hashId, displayName: brokerName });
 *   await db.doc(`companies/${companyId}/brokers/${hashId}`).set(...);
 *   ```
 *
 * The check is intentionally strict — empty strings, whitespace-only, and
 * the sentinel literals are all refused. Any future sentinel discovered in
 * production should be added to BROKER_SENTINEL_NAMES / _IDS, not worked
 * around via the caller.
 */
function assertBrokerCreatePayload(payload) {
    // Resolve the effective display name from the candidate fields the broker
    // doc may carry. Any one of them being a sentinel rejects the write.
    const displayCandidates = [
        { field: 'displayName', value: payload.displayName },
        { field: 'currentName', value: payload.currentName },
        { field: 'legalName', value: payload.legalName },
    ];
    for (const { field, value } of displayCandidates) {
        if (value === undefined)
            continue; // field not supplied — fine
        const trimmed = (value ?? '').toString().trim();
        if (trimmed === '') {
            throw new BrokerInvariantViolation('displayName', String(value ?? ''), `Broker ${field} is empty. ` +
                `Refusing to create broker with no identity. Source bug — fix the upstream caller.`);
        }
        if (exports.BROKER_SENTINEL_NAMES.has(trimmed.toLowerCase())) {
            throw new BrokerInvariantViolation('displayName', trimmed, `Broker ${field} is a reserved sentinel value (${JSON.stringify(trimmed)}). ` +
                `These appear when the parser cannot identify a real broker — historically ` +
                `they polluted the broker dropdown. Fix the upstream caller; do not synthesise ` +
                `a broker doc for unidentifiable rows.`);
        }
    }
    if (payload.id !== undefined && payload.id !== null) {
        const trimmedId = payload.id.toString().trim();
        if (trimmedId === '') {
            throw new BrokerInvariantViolation('id', '', `Broker id is empty. Refusing to write a broker doc with no stable identifier.`);
        }
        if (exports.BROKER_SENTINEL_IDS.has(trimmedId.toLowerCase())) {
            throw new BrokerInvariantViolation('id', trimmedId, `Broker id is a reserved sentinel value (${JSON.stringify(trimmedId)}). ` +
                `Sentinel ids appear when the parser cannot derive a real broker hash — ` +
                `historically they polluted RBAC, auth claims, and dashboards. ` +
                `Fix the upstream caller.`);
        }
    }
}
/**
 * Convenience: returns the sentinel diagnostic without throwing. Useful for
 * non-fatal probe paths (e.g. data-integrity scans that count violations).
 */
function describeBrokerSentinel(payload) {
    try {
        assertBrokerCreatePayload(payload);
        return null;
    }
    catch (err) {
        if (err instanceof BrokerInvariantViolation)
            return err.message;
        throw err;
    }
}
